"use client";

import { useTranslation } from "react-i18next";
import { Icon } from "@iconify-icon/react";
import { motion } from "motion/react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface LessonEndedDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  wasExtended?: boolean;
  extensionMinutes?: number | null;
}

export default function LessonEndedDialog({ isOpen, onClose, onConfirm }: LessonEndedDialogProps) {
  const { t } = useTranslation(["common", "classroom"]);

  return (
    <AlertDialog open={isOpen} onOpenChange={onClose}>
      <AlertDialogContent className="bg-foreground/90 max-w-md rounded-xl border-none backdrop-blur-xl">
        <AlertDialogHeader>
          <motion.div
            animate={{ scale: 1, opacity: 1 }}
            className="mb-2 flex justify-center"
            initial={{ scale: 0.9, opacity: 0 }}
          >
            <div className="bg-primary/10 text-accent-foreground flex h-12 w-12 items-center justify-center rounded-full">
              <Icon height="24" icon="lucide:clock" width="24" />
            </div>
          </motion.div>
          <AlertDialogTitle className="text-accent-foreground text-center">
            {t("classroom:lessonEndedTitle", { defaultValue: "Lesson Ended" })}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-muted-foreground text-center">
            {t("classroom:lessonEndedDescription", {
              defaultValue: "The lesson has ended. Thank you for participating!",
            })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-col space-y-2 sm:space-y-0">
          <AlertDialogAction
            className="bg-accent-foreground text-primary hover:text-side-accent hover:bg-accent-foreground/75 w-full"
            onClick={onConfirm}
          >
            {t("common:close", { defaultValue: "Close" })}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
